import React, { useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, TextArea } from '@socialchorus/shared-ui-components';
import { getPendo } from '../../common/use-analytics';

const MAX_HEIGHT = 96;

interface ChatInputProps {
  messageInput: string;
  onMessageChange: (value: string) => void;
  onSendMessage: () => void;
  isSending: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({
  messageInput,
  onMessageChange,
  onSendMessage,
  isSending,
}) => {
  const { t } = useTranslation();
  const submitMethodRef = useRef<'keyboard' | 'click'>('click');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevIsSendingRef = useRef(isSending);

  const resizeTextarea = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const scrollHeight = textarea.scrollHeight;
    textarea.style.height = `${scrollHeight}px`;
    textarea.style.overflowY = scrollHeight > MAX_HEIGHT ? 'auto' : 'hidden';
  }, []);

  useEffect(() => {
    resizeTextarea();
  }, [messageInput, resizeTextarea]);

  useEffect(() => {
    const wasSending = prevIsSendingRef.current;
    prevIsSendingRef.current = isSending;

    if (wasSending && !isSending) {
      if (containerRef.current?.contains(document.activeElement)) {
        textareaRef.current?.focus();
      }
    }
  }, [isSending]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (messageInput.trim() && !isSending) {
        submitMethodRef.current = 'keyboard';
        e.currentTarget.closest('form')?.requestSubmit();
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (messageInput.trim() && !isSending) {
      getPendo()?.track('chatbot-message-sent', {
        triggerMethod: submitMethodRef.current,
      });
      onSendMessage();
    }
    submitMethodRef.current = 'click';
  };

  return (
    <div className="chatbot__input" ref={containerRef}>
      <div className="chatbot__input-content">
        <form onSubmit={handleSubmit} data-pendo-id="chatbot-input-form">
          <div className="chatbot__input-form">
            <div className="chatbot__textarea-wrapper">
              <div className="chatbot__textarea-field">
                <TextArea
                  ref={textareaRef}
                  id="chatbot-message-input"
                  label="Message"
                  value={messageInput}
                  onChange={onMessageChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your message..."
                  noResize
                  fullWidth
                  rows={1}
                  style={{ maxHeight: `${MAX_HEIGHT}px`, overflowY: 'hidden' }}
                />
              </div>
              <span data-pendo-id="chatbot-send-button">
                <IconButton
                  type="submit"
                  variant="default"
                  size="default"
                  iconName="send"
                  label={t('chatbot.sendMessage')}
                  disabled={!messageInput.trim() || isSending}
                />
              </span>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChatInput;
